import { useState, useEffect } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import SpaceScene from '../components/Hero/SpaceScene'
import { ref, push } from 'firebase/database'
import { database } from '../config/firebase'
import Navbar from '../components/Navbar/Navbar'
import styles from './StudentApplication.module.css'

type Step = 'welcome' | 'firstName' | 'lastName' | 'phone' | 'email' | 'cursor' | 'firebase' | 'imgbb' | 'linkedin' | 'project' | 'thankyou'

const StudentApplication = () => {
  const { t } = useLanguage()
  const [currentStep, setCurrentStep] = useState<Step>('welcome')
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    cursorExperience: '',
    firebaseExperience: '',
    imgbbExperience: '',
    linkedinExperience: '',
    projectExperience: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [isFadingIn, setIsFadingIn] = useState(true)

  const handleInputChange = (value: string) => {
    const stepFieldMap: Record<Step, keyof typeof formData> = {
      welcome: 'firstName',
      firstName: 'firstName',
      lastName: 'lastName',
      phone: 'phone',
      email: 'email',
      cursor: 'cursorExperience',
      firebase: 'firebaseExperience',
      imgbb: 'imgbbExperience',
      linkedin: 'linkedinExperience',
      project: 'projectExperience',
      thankyou: 'firstName'
    }

    const field = stepFieldMap[currentStep]
    if (field) {
      setFormData(prev => ({ ...prev, [field]: value }))
    }
  }

  const handleNext = () => {
    setIsFadingOut(true)
    setIsFadingIn(false)
    setTimeout(() => {
      const steps: Step[] = ['welcome', 'firstName', 'lastName', 'phone', 'email', 'cursor', 'firebase', 'imgbb', 'linkedin', 'project', 'thankyou']
      const currentIndex = steps.indexOf(currentStep)
      
      if (currentIndex < steps.length - 1) {
        setCurrentStep(steps[currentIndex + 1])
        // Kısa bir delay sonra fade in başlasın
        setTimeout(() => {
          setIsFadingOut(false)
          setIsFadingIn(true)
        }, 50)
      }
    }, 500)
  }

  // Reset fade when step changes
  useEffect(() => {
    setIsFadingIn(true)
    setIsFadingOut(false)
    
    // Input'ları temizle (görsel olarak)
    const stepFieldMap: Record<Step, keyof typeof formData> = {
      welcome: 'firstName',
      firstName: 'firstName',
      lastName: 'lastName',
      phone: 'phone',
      email: 'email',
      cursor: 'cursorExperience',
      firebase: 'firebaseExperience',
      imgbb: 'imgbbExperience',
      linkedin: 'linkedinExperience',
      project: 'projectExperience',
      thankyou: 'firstName'
    }
    
    const field = stepFieldMap[currentStep]
    if (field && (currentStep === 'cursor' || currentStep === 'firebase' || currentStep === 'imgbb' || currentStep === 'linkedin' || currentStep === 'project')) {
      // Sadece soru inputlarını temizle, diğerlerini koru
      setFormData(prev => ({ ...prev, [field]: '' }))
    }
  }, [currentStep])

  const handleInputSubmit = () => {
    const stepFieldMap: Record<Step, keyof typeof formData> = {
      welcome: 'firstName',
      firstName: 'firstName',
      lastName: 'lastName',
      phone: 'phone',
      email: 'email',
      cursor: 'cursorExperience',
      firebase: 'firebaseExperience',
      imgbb: 'imgbbExperience',
      linkedin: 'linkedinExperience',
      project: 'projectExperience',
      thankyou: 'firstName'
    }
    
    const field = stepFieldMap[currentStep]
    const currentValue = field ? formData[field] as string : ''
    
    if (currentValue && currentValue.trim()) {
      // Önce veriyi kaydet, sonra input'u temizle ve ilerle
      setTimeout(() => {
        // Input'u temizle (görsel olarak)
        if (field) {
          setFormData(prev => ({ ...prev, [field]: '' }))
        }
        
        setTimeout(() => {
          if (currentStep === 'project') {
            // Project'ten sonra direkt teşekkürler sayfasına geç
            setCurrentStep('thankyou')
            handleSubmit()
          } else {
            handleNext()
          }
        }, 100)
      }, 100)
    }
  }


  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const applicationsRef = ref(database, 'studentApplications')
      await push(applicationsRef, {
        ...formData,
        timestamp: new Date().toISOString()
      })
      setCurrentStep('thankyou')
    } catch (error) {
      console.error('Error submitting application:', error)
      alert(t('studentApplication.error'))
    } finally {
      setIsSubmitting(false)
    }
  }


  const isValidEmail = (email: string) => {
    if (!email.includes('@')) return false
    const afterAt = email.split('@')[1]
    return afterAt && afterAt.includes('.edu')
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (currentStep === 'email') {
        // Email validation
        if (isValidEmail(formData.email)) {
          handleNext()
        }
      } else if (currentStep === 'cursor' || currentStep === 'firebase' || currentStep === 'imgbb' || currentStep === 'linkedin' || currentStep === 'project') {
        handleInputSubmit()
      } else if (formData[currentStep as keyof typeof formData]) {
        handleNext()
      }
    }
  }

  const getCurrentTitle = () => {
    switch (currentStep) {
      case 'welcome':
        return t('studentApplication.welcomeTitle')
      case 'firstName':
        return t('studentApplication.firstNameTitle')
      case 'lastName':
        return t('studentApplication.lastNameTitle')
      case 'phone':
        return t('studentApplication.phoneTitle')
      case 'email':
        return t('studentApplication.emailTitle')
      case 'cursor':
        return t('studentApplication.cursorTitleLabel')
      case 'firebase':
        return t('studentApplication.firebaseTitleLabel')
      case 'imgbb':
        return t('studentApplication.imgbbTitleLabel')
      case 'linkedin':
        return t('studentApplication.linkedinTitleLabel')
      case 'project':
        return t('studentApplication.projectTitleLabel')
      case 'thankyou':
        return t('studentApplication.thankyouTitle')
      default:
        return ''
    }
  }

  const getCurrentSubtitle = () => {
    switch (currentStep) {
      case 'welcome':
        return t('studentApplication.welcomeSubtitle')
      case 'firstName':
        return t('studentApplication.firstNameDescription')
      case 'lastName':
        return t('studentApplication.lastNameDescription')
      case 'phone':
        return t('studentApplication.phoneDescription')
      case 'email':
        return t('studentApplication.emailDescription')
      case 'cursor':
        return t('studentApplication.cursorTitle')
      case 'firebase':
        return t('studentApplication.firebaseTitle')
      case 'imgbb':
        return t('studentApplication.imgbbTitle')
      case 'linkedin':
        return t('studentApplication.linkedinTitle')
      case 'project':
        return t('studentApplication.projectTitle')
      case 'thankyou':
        return t('studentApplication.thankyouSubtitle')
      default:
        return ''
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 'welcome':
        return (
          <div className={styles.stepContent}>
            <button className={styles.ctaButton} onClick={handleNext}>
              {t('studentApplication.readyButton')}
            </button>
          </div>
        )

      case 'firstName':
        return (
          <div className={styles.stepContent}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                className={styles.input}
                placeholder={t('studentApplication.firstNamePlaceholder')}
                value={formData.firstName}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyPress={handleKeyPress}
                autoFocus
              />
              <button 
                className={styles.sendButton} 
                onClick={handleNext}
                disabled={!formData.firstName.trim()}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
          </div>
        )

      case 'lastName':
        return (
          <div className={styles.stepContent}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                className={styles.input}
                placeholder={t('studentApplication.lastNamePlaceholder')}
                value={formData.lastName}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyPress={handleKeyPress}
                autoFocus
              />
              <button 
                className={styles.sendButton} 
                onClick={handleNext}
                disabled={!formData.lastName.trim()}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
          </div>
        )

      case 'phone':
        return (
          <div className={styles.stepContent}>
            <div className={styles.inputWrapper}>
              <input
                type="tel"
                className={styles.input}
                placeholder={t('studentApplication.phonePlaceholder')}
                value={formData.phone}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyPress={handleKeyPress}
                autoFocus
              />
              <button 
                className={styles.sendButton} 
                onClick={handleNext}
                disabled={!formData.phone.trim()}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
          </div>
        )

      case 'email':
        return (
          <div className={styles.stepContent}>
            <div className={styles.inputWrapper}>
              <input
                type="email"
                className={styles.input}
                placeholder={t('studentApplication.emailPlaceholder')}
                value={formData.email}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyPress={handleKeyPress}
                autoFocus
              />
              <button 
                className={styles.sendButton} 
                onClick={handleNext}
                disabled={!isValidEmail(formData.email)}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
            {formData.email && !isValidEmail(formData.email) && (
              <p className={styles.errorText}>{t('studentApplication.emailError')}</p>
            )}
          </div>
        )

      case 'cursor':
        return (
          <div className={styles.stepContent}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                className={styles.input}
                placeholder={t('studentApplication.answerPlaceholder')}
                value={formData.cursorExperience}
                onChange={(e) => {
                  if (e.target.value.length <= 20) {
                    handleInputChange(e.target.value)
                  }
                }}
                onKeyPress={handleKeyPress}
                maxLength={20}
                autoFocus
              />
              <button 
                className={styles.sendButton} 
                onClick={handleInputSubmit}
                disabled={!formData.cursorExperience.trim()}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
          </div>
        )

      case 'firebase':
        return (
          <div className={styles.stepContent}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                className={styles.input}
                placeholder={t('studentApplication.answerPlaceholder')}
                value={formData.firebaseExperience}
                onChange={(e) => {
                  if (e.target.value.length <= 20) {
                    handleInputChange(e.target.value)
                  }
                }}
                onKeyPress={handleKeyPress}
                maxLength={20}
                autoFocus
              />
              <button 
                className={styles.sendButton} 
                onClick={handleInputSubmit}
                disabled={!formData.firebaseExperience.trim()}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
            {isSubmitting && (
              <p className={styles.submittingText}>{t('studentApplication.submitting')}</p>
            )}
          </div>
        )

      case 'imgbb':
        return (
          <div className={styles.stepContent}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                className={styles.input}
                placeholder={t('studentApplication.answerPlaceholder')}
                value={formData.imgbbExperience}
                onChange={(e) => {
                  if (e.target.value.length <= 20) {
                    handleInputChange(e.target.value)
                  }
                }}
                onKeyPress={handleKeyPress}
                maxLength={20}
                autoFocus
              />
              <button 
                className={styles.sendButton} 
                onClick={handleInputSubmit}
                disabled={!formData.imgbbExperience.trim()}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
            {isSubmitting && (
              <p className={styles.submittingText}>{t('studentApplication.submitting')}</p>
            )}
          </div>
        )

      case 'linkedin':
        return (
          <div className={styles.stepContent}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                className={styles.input}
                placeholder={t('studentApplication.answerPlaceholder')}
                value={formData.linkedinExperience}
                onChange={(e) => {
                  if (e.target.value.length <= 20) {
                    handleInputChange(e.target.value)
                  }
                }}
                onKeyPress={handleKeyPress}
                maxLength={20}
                autoFocus
              />
              <button 
                className={styles.sendButton} 
                onClick={handleInputSubmit}
                disabled={!formData.linkedinExperience.trim()}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
          </div>
        )

      case 'project':
        return (
          <div className={styles.stepContent}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                className={styles.input}
                placeholder={t('studentApplication.answerPlaceholder')}
                value={formData.projectExperience}
                onChange={(e) => {
                  if (e.target.value.length <= 20) {
                    handleInputChange(e.target.value)
                  }
                }}
                onKeyPress={handleKeyPress}
                maxLength={20}
                autoFocus
              />
              <button 
                className={styles.sendButton} 
                onClick={handleInputSubmit}
                disabled={!formData.projectExperience.trim()}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
          </div>
        )

      case 'thankyou':
        return (
          <div className={styles.stepContent}>
            <div className={styles.thankyouMessage}>
              <p className={styles.thankyouText}>{t('studentApplication.thankyouSubtitle')}</p>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <>
      <Navbar />
      <section className={styles.hero}>
        <div className={styles.spaceBackground}>
          <SpaceScene />
        </div>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1 className={`${styles.heroTitle} ${isFadingOut ? styles.fadeOut : isFadingIn ? styles.fadeIn : ''}`}>
              {getCurrentTitle()}
            </h1>
            {getCurrentSubtitle() && (
              <p className={`${styles.subtitle} ${isFadingOut ? styles.fadeOut : isFadingIn ? styles.fadeIn : ''}`}>{getCurrentSubtitle()}</p>
            )}
            {renderStepContent()}
          </div>
        </div>
      </section>
    </>
  )
}

export default StudentApplication

